import { useEffect, useState } from 'react';
import { api, heldForApproval } from '../lib/api';
import { Card, ErrorNote, Field, Modal, useToast } from './ui';

export interface RoleRow {
  id: string;
  key: string;
  name: string;
  description: string;
  system: boolean;
  permissions: string[];
  users: number;
}

export interface ScreenDef {
  key: string;
  label: string;
  group: string;
  actions: Array<{ key: string; label: string }>;
}

/**
 * The permission matrix. Rows are screens with their actions indented,
 * columns are roles, cells are grants. Owner is fixed. Edits are held per
 * role until saved so a half-finished column never goes live.
 */
export function RoleMatrix({ roles, screens, canEdit, onChanged }: {
  roles: RoleRow[]; screens: ScreenDef[]; canEdit: boolean; onChanged: () => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<RoleRow | null>(null);

  useEffect(() => {
    setDraft(Object.fromEntries(roles.map((r) => [r.id, new Set(r.permissions)])));
  }, [roles]);

  const dirty = (r: RoleRow) => {
    const d = draft[r.id]; if (!d) return false;
    if (d.size !== r.permissions.length) return true;
    return r.permissions.some((p) => !d.has(p));
  };

  function toggle(r: RoleRow, perm: string) {
    if (!canEdit || r.key === 'owner') return;
    setDraft((cur) => {
      const next = new Set(cur[r.id] ?? []);
      const [screen] = perm.split(':');
      if (next.has(perm)) {
        next.delete(perm);
        // Removing view removes every action on the screen: without view nothing is reachable.
        if (perm.endsWith(':view')) for (const p of [...next]) if (p.startsWith(screen + ':')) next.delete(p);
      } else {
        next.add(perm);
        next.add(`${screen}:view`);
      }
      return { ...cur, [r.id]: next };
    });
  }

  async function save(r: RoleRow) {
    setSaving(r.id);
    try {
      const res = await api.put(`/v1/roles/${r.id}`, { permissions: [...(draft[r.id] ?? [])] });
      toast(heldForApproval(res) ? `Sent for approval. A second person must approve the change to ${r.name}.` : `${r.name} saved.`, 'ok');
      onChanged();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setSaving(null);
    }
  }

  async function remove(r: RoleRow) {
    if (!window.confirm(`Delete the role "${r.name}"?`)) return;
    try {
      await api.delete(`/v1/roles/${r.id}`);
      toast(`${r.name} deleted.`, 'ok');
      onChanged();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const groups = [...new Set(screens.map((s) => s.group))];

  return (
    <Card title="Roles">
      <div className="table-scroll">
        <table className="data matrix">
          <thead>
            <tr>
              <th>Screen and action</th>
              {roles.map((r) => (
                <th key={r.id} className="matrix-role">
                  <div className="matrix-role-name">{r.name}</div>
                  <div className="small muted">{r.users} {r.users === 1 ? 'person' : 'people'}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <RowGroup key={g} label={g} screens={screens.filter((s) => s.group === g)} roles={roles} draft={draft} canEdit={canEdit} toggle={toggle} />
            ))}
          </tbody>
          {canEdit && (
            <tfoot>
              <tr>
                <td />
                {roles.map((r) => (
                  <td key={r.id} className="matrix-cell">
                    {r.key === 'owner' ? (
                      <span className="small muted">Fixed</span>
                    ) : (
                      <div className="u-actions" style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                        <button className="small primary" disabled={!dirty(r) || saving === r.id} onClick={() => save(r)}>
                          {saving === r.id ? 'Saving…' : 'Save'}
                        </button>
                        {!r.system && <button className="small" onClick={() => setRenaming(r)}>Rename</button>}
                        {!r.system && <button className="small danger" onClick={() => remove(r)}>Delete</button>}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {canEdit && (
        <div style={{ marginTop: 14 }}>
          <button onClick={() => setAdding(true)}>Add role</button>
        </div>
      )}
      {adding && <RoleModal onClose={() => setAdding(false)} onDone={() => { setAdding(false); onChanged(); }} />}
      {renaming && <RoleModal role={renaming} onClose={() => setRenaming(null)} onDone={() => { setRenaming(null); onChanged(); }} />}
    </Card>
  );
}

function RowGroup({ label, screens, roles, draft, canEdit, toggle }: {
  label: string; screens: ScreenDef[]; roles: RoleRow[]; draft: Record<string, Set<string>>;
  canEdit: boolean; toggle: (r: RoleRow, perm: string) => void;
}) {
  return (
    <>
      <tr className="matrix-group"><td colSpan={roles.length + 1}>{label}</td></tr>
      {screens.map((s) => s.actions.map((a, i) => {
        const perm = `${s.key}:${a.key}`;
        return (
          <tr key={perm} className={i === 0 ? 'matrix-screen' : 'matrix-action'}>
            <td>
              {i === 0 ? <strong>{s.label}</strong> : null}
              <span className={i === 0 ? 'small muted' : 'small'} style={{ marginLeft: i === 0 ? 8 : 20 }}>
                {i === 0 ? a.label : a.label}
              </span>
            </td>
            {roles.map((r) => {
              const on = r.key === 'owner' || (draft[r.id]?.has(perm) ?? false);
              const locked = !canEdit || r.key === 'owner';
              return (
                <td key={r.id} className="matrix-cell">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={locked}
                    aria-label={`${r.name}: ${s.label}, ${a.label}`}
                    onChange={() => toggle(r, perm)}
                  />
                </td>
              );
            })}
          </tr>
        );
      }))}
    </>
  );
}

function RoleModal({ role, onClose, onDone }: { role?: RoleRow; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      if (role) await api.put(`/v1/roles/${role.id}`, { name, description });
      else await api.post('/v1/roles', { name, description, permissions: ['dashboard:view'] });
      onDone();
    } catch (err) {
      setError((err as Error).message); setBusy(false);
    }
  }

  return (
    <Modal
      title={role ? 'Rename role' : 'New role'}
      onClose={onClose}
      footer={<><button onClick={onClose}>Cancel</button><button className="primary" onClick={submit} disabled={busy || !name.trim()}>{busy ? 'Saving…' : role ? 'Save' : 'Create role'}</button></>}
    >
      <Field label="Name"><input type="text" value={name} autoFocus onChange={(e) => setName(e.target.value)} maxLength={60} /></Field>
      <Field label="Description"><input type="text" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} /></Field>
      {!role && <div className="small muted">Grants are ticked in the matrix after the role is created.</div>}
      <ErrorNote error={error} />
    </Modal>
  );
}
